import { useEffect, useMemo, useRef, useState } from 'react';

import { Star } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { format, parseISO } from 'date-fns';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowVirtualizer,
  MRT_SortingState,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { AlertType } from 'src/types/alert';
import { Favorite } from 'src/types/favorite';

type DefaultTableProps = {
  favoriteType: { name: string; label: string; singular: string };
  onRowClick: (favorite: Favorite, title: string) => void;
  refetch: () => void;
  isLoading: boolean;
  data: {
    favorite: Favorite;
  }[];
};

export function DefaultTable({
  favoriteType,
  data,
  onRowClick,
  refetch,
  isLoading,
}: DefaultTableProps) {
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);

  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [batchSwitch, setBatchSwitch] = useState<{
    [key: string]: boolean;
  }>({});
  const [openDialog, setOpenDialog] = useState(false);
  const dispatch = useDispatch();
  const [currentData, setCurrentData] = useState<typeof data>(data);
  const [selectedItem, setSelectedItem] = useState<{
    type: AlertType;
    enabled: boolean;
  } | null>(null);

  function getFavoriteTypeData(favorite: Favorite) {
    if (favoriteType.name === 'institution') {
      return {
        headerName: 'Instituição',
        id: favorite.institution?.id,
        name: favorite.institution?.fantasyName,
        slug: favorite.institution?.slug,
        link: `/instituicao/${favorite.institution?.slug}/${favorite.institution?.id}`,
      };
    }
    if (favoriteType.name === 'professional') {
      return {
        headerName: 'Profissional',
        id: favorite.professional?.id,
        name: favorite.professional?.fullName,
        slug: favorite.professional?.slug,
        link: `/profissional/${favorite.professional?.slug}/${favorite.professional?.id}`,
      };
    }
    if (favoriteType.name === 'article') {
      return {
        headerName: 'Artigo',
        id: favorite.article?.id,
        name: favorite.article?.title,
        slug: favorite.article?.slug,
        link: `/artigo/${favorite.article?.slug}/${favorite.article?.id}`,
      };
    }

    if (favoriteType.name === 'event') {
      return {
        headerName: 'Evento',
        id: favorite.event?.id,
        name: favorite.event?.title,
        slug: favorite.event?.slug,
        link: `/evento/${favorite.event?.slug}/${favorite.event?.id}`,
      };
    }

    if (favoriteType.name === 'service') {
      return {
        headerName: 'Serviço',
        id: favorite.service?.id,
        name: favorite.service?.title,
        slug: favorite.service?.slug,
        link: `/marketplace/servico/${favorite.service?.slug}/${favorite.service?.id}`,
      };
    }
  }

  const columns = useMemo<
    MRT_ColumnDef<{
      favorite: Favorite;
    }>[]
  >(
    () => [
      {
        header: 'Ação',
        size: 80,
        Cell({ row }) {
          return (
            <IconButton
              sx={{
                color: '#1D1D1D',
                fontSize: '12px',
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: 0,
                padding: 0,
                justifyContent: 'flex-start',
                '&:hover': {
                  background: 'transparent',
                },
                p: 2,
              }}
              onClick={() =>
                onRowClick(
                  row.original.favorite,
                  getFavoriteTypeData(row.original.favorite)?.name ?? '',
                )
              }>
              <Star color="primary" />
            </IconButton>
          );
        },
      },
      {
        accessorFn(originalRow) {
          return getFavoriteTypeData(originalRow.favorite)?.name;
        },
        header: favoriteType.singular,
        size: 300,
        Cell({ row }) {
          const data = getFavoriteTypeData(row.original.favorite);
          return (
            <Link
              to={data?.link ?? ''}
              style={{
                fontSize: '14px',
                fontWeight: 400,
                color: '#1D1D1D',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {data?.name}
            </Link>
          );
        },
      },
      {
        accessorKey: 'favorite.createdAt',
        header: 'Adicionado em',
        size: 150,
        Cell({ row }) {
          return row.original.favorite.createdAt
            ? format(parseISO(row.original.favorite.createdAt), 'dd/MM/yyyy')
            : '';
        },
      },
    ],
    [currentData, batchSwitch],
  );

  useEffect(() => {
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [sorting]);

  useEffect(() => {
    if (data) {
      setCurrentData(data);
    }
  }, [data]);

  const table = useMaterialReactTable({
    columns,
    data: currentData ?? [], //10,000 rows
    enableBottomToolbar: false,
    enableGlobalFilterModes: true,
    enablePagination: false,
    enableRowNumbers: false,
    enableRowVirtualization: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: '500px',
      },
    },
    onSortingChange: setSorting,
    state: {
      isLoading,
      sorting,
      columnOrder: ['Ação', favoriteType.singular],
    },
    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 5 },
    enableColumnActions: false,
    enableGlobalFilter: true,
    enableColumnDragging: false,
    enableDensityToggle: false,
    enableExpandAll: false,
    enableFullScreenToggle: false,
    enableEditing: false,
    enableHiding: false,
    enableColumnOrdering: false,
    enableTopToolbar: true,
    localization: MRT_Localization_PT_BR,
    muiTablePaperProps: {
      elevation: 0,
    },
    muiTableHeadRowProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.grey[200],
      }),
    },
  });

  return <MaterialReactTable table={table} />;
}
